import { useLazyQuery } from "@apollo/client";
import { ContactPointSystem, type SearchPatientInput } from "elephant-web/gql";
import { useCallback, useMemo } from "react";
import type {
  SearchPatientsElasticQuery,
  SearchPatientsElasticQueryVariables,
} from "./__generated__/patient-search-operations.gql";
import { SearchPatientsElasticDocument } from "./__generated__/patient-search-operations.gql";

export type PatientSearchCriteria = {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
  phoneNumber?: string;
  nationalIdNumber?: string;
};

export type PatientSearchResult = {
  patientId: string;
  displayName: string;
  dob: string | null;
  nationalIdNumber: string | null;
  phone: string | null;
};

const toPatientInput = (
  criteria: PatientSearchCriteria,
): SearchPatientInput => {
  const input: { -readonly [K in keyof PatientSearchCriteria]?: string } = {};
  for (const key of Object.keys(criteria) as (keyof PatientSearchCriteria)[]) {
    const trimmed = criteria[key]?.trim();
    if (trimmed) {
      input[key] = trimmed;
    }
  }
  return input;
};

export const usePatientSearch = () => {
  const [runSearch, { data, loading }] = useLazyQuery<
    SearchPatientsElasticQuery,
    SearchPatientsElasticQueryVariables
  >(SearchPatientsElasticDocument, { fetchPolicy: "network-only" });

  const search = useCallback(
    (criteria: PatientSearchCriteria) => {
      const patient = toPatientInput(criteria);
      if (Object.keys(patient).length === 0) return;

      void runSearch({
        variables: {
          patient,
          limit: 20,
        },
      });
    },
    [runSearch],
  );

  const patients = useMemo<PatientSearchResult[]>(
    () =>
      (data?.searchPatients ?? [])
        .filter((p): p is NonNullable<typeof p> => p !== null)
        .map((p) => {
          const { firstname = "", middlename, lastname = "" } = p.person.name;
          const parts = [firstname, middlename, lastname].filter(Boolean);
          const phone =
            p.person.contactPoints.find(
              (cp) => cp?.system === ContactPointSystem.Phone,
            )?.value ?? null;
          return {
            patientId: p.uuid,
            displayName: parts.join(" ") || "Unknown patient",
            dob: p.person.dob || null,
            nationalIdNumber: p.person.nationalIdNumber ?? null,
            phone,
          };
        }),
    [data],
  );

  return { search, patients, loading };
};
